import React from 'react';
import {
  FlatList,
  Platform,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { useScrollToTop } from '@react-navigation/native';

import { PageItemType } from '@src/api/enums/pageItemType';
import Carousel, { Pagination } from '@src/components/Carousel';
import Container from '@src/components/Container';
import Cover from '@src/components/Cover';
import NewEntryFab from '@src/components/NewEntryFab';
import Separator from '@src/components/Separator';
import Slide from '@src/components/Slide';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import { DynamicPageItemAPIProduce } from '@src/enums';
import useAnimatedHeader from '@src/hooks/useAnimatedHeader';
import useHandleEntityPress from '@src/hooks/useHandleEntityPress';
import { Slide as TSlide } from '@src/types';
import { useIsDrawerOpen } from '@react-navigation/drawer';
import HeroItems from './HeroItems';
import JourneyItems from './JourneyItems';
import PostItems from './PostItems';
import PrimeItems from './PrimeItems';
import Shortcuts from './Shortcuts';
import { CarouselContainer } from './styled';
import TourItems from './TourItems';
import { Props } from './types';
import useHome from './useHome';
import UserItems from './UserItems';
import useSlides from './useSlides';

const isWeb = Platform.OS === 'web';

const Screen: React.FC<Props> = () => {
  const home = useHome();
  const isOpen = useIsDrawerOpen();
  const topSlides = useSlides(1);
  const ref = React.useRef(null);
  const carouselRef = React.useRef(null);
  const handleScroll = useAnimatedHeader();
  const handleEntityPress = useHandleEntityPress();
  const dimensions = useWindowDimensions();
  const [activeSlide, setActiveSlide] = React.useState(0);
  let sizeWidth = dimensions.width;
  const { itemWidth, sliderWidth } = React.useMemo(
    () => ({
      itemWidth: sizeWidth - 50,
      sliderWidth: sizeWidth,
    }),
    [sizeWidth]
  );
  useScrollToTop(ref);

  const onSnapToItemHandler = React.useCallback(
    (index: number) => {
      setActiveSlide(topSlides.data.length - index - 1);
    },
    [topSlides.data.length]
  );

  return (
    <>
      <VisibleStatusBar />
      <Container>
        <ScrollView
          ref={ref}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          onScroll={handleScroll}
        >
          <CarouselContainer>
            {topSlides.loading ? null : (
              <>
                <Carousel<TSlide>
                  loop
                  firstItem={0}
                  initialNumToRender={topSlides.data.length}
                  windowSize={5}
                  // @ts-ignore
                  ref={carouselRef}
                  data={topSlides.data}
                  itemWidth={itemWidth ? itemWidth : 250}
                  sliderWidth={sliderWidth ? sliderWidth : 300}
                  // onSnapToItem={onSnapToItemHandler}
                  onBeforeSnapToItem={onSnapToItemHandler}
                  onScrollToIndexFailed={({ index }) =>
                    onSnapToItemHandler(index)
                  }
                  inactiveSlideScale={0.75}
                  // activeAnimationOptions={{
                  //   friction: 4,
                  //   tension: 40,
                  // }}
                  directionalLockEnabled
                  enableMomentum={false}
                  // contentContainerCustomStyle={{ marginTop: 16 }}
                  renderItem={({ item, index }) => {
                    return (
                      <Slide
                        onPress={handleEntityPress(item)}
                        source={item.cover.source}
                      />
                    );
                  }}
                />
                <Pagination
                  dotsLength={topSlides.data.length}
                  activeDotIndex={activeSlide}
                  // @ts-ignore
                  carouselRef={carouselRef}
                />
              </>
            )}
          </CarouselContainer>
          <Shortcuts />

          {/* <Separator level={2} /> */}
          <FlatList
            data={home}
            renderItem={({ item, index }: { item: any; index: number }) => {
              switch (item.type) {
                case PageItemType.COVER: {
                  const data = item.data;
                  return (
                    <Cover
                      key={`cover-${index}`}
                      source={data.cover.source}
                      onPress={handleEntityPress(data)}
                    />
                  );
                }

                case PageItemType.API: {
                  const data = item.data;

                  switch (data.produces) {
                    case DynamicPageItemAPIProduce.Tour: {
                      return (
                        <>
                          <TourItems key={`tours-${index}`} data={data} />
                          <Separator key={`separator-${index}`} />
                        </>
                      );
                    }
                    case DynamicPageItemAPIProduce.User: {
                      return (
                        <>
                          <UserItems key={`users-${index}`} data={data} />
                          <Separator key={`separator-${index}`} />
                        </>
                      );
                    }
                    case DynamicPageItemAPIProduce.Post: {
                      return (
                        <>
                          <PostItems key={`posts-${index}`} data={data} />
                          <Separator key={`separator-${index}`} />
                        </>
                      );
                    }
                    case DynamicPageItemAPIProduce.Journey: {
                      return (
                        <>
                          <JourneyItems key={`journeys-${index}`} data={data} />
                          <Separator key={`separator-${index}`} />
                        </>
                      );
                    }
                    case DynamicPageItemAPIProduce.Prime: {
                      return (
                        <>
                          <PrimeItems key={`primes-${index}`} data={data} />
                          <Separator key={`separator-${index}`} />
                        </>
                      );
                    }
                    case DynamicPageItemAPIProduce.Hero: {
                      return (
                        <>
                          <HeroItems key={`heros-${index}`} data={data} />
                          <Separator key={`separator-${index}`} />
                        </>
                      );
                    }
                    default: {
                      return null;
                    }
                  }
                }

                default:
                  return null;
              }
            }}
            keyExtractor={(item: any) => String(item.id)}
            // onLayout={this.onLayout}
            // numColumns={columnCount}
            // style={styles.list}
          />
          {/* // {home.map((item, index) => { */}
          {/*
          // })} */}

          <Separator />
        </ScrollView>
        {!isWeb && <NewEntryFab />}
      </Container>
    </>
  );
};

Screen.displayName = 'Tours';

export default Screen;
